import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Area, AreaChart } from 'recharts'
import { ScoreHistoryChartProps } from '@/types/components'
import { ScoreBand } from '@/types'
import { format, parseISO } from 'date-fns'

const BAND_THRESHOLDS = {
  'A+': 90,
  'A': 80,
  'B+': 70,
  'B': 60,
  'C+': 50,
  'C': 40,
  'D': 0
}

const BAND_COLORS = {
  'A+': '#10b981', // green-500
  'A': '#10b981',
  'B+': '#3b82f6', // blue-500
  'B': '#3b82f6',
  'C+': '#f59e0b', // amber-500
  'C': '#f97316', // orange-500
  'D': '#ef4444'  // red-500
}

export function ScoreHistoryChart({
  data,
  timeRange = '30d',
  showBands = true,
  showTrendLine = true,
  height = 400,
  className
}: ScoreHistoryChartProps) {
  // Format data for chart
  const chartData = data.map(item => ({
    ...item,
    date: format(parseISO(item.date), 'MMM dd'),
    fullDate: item.date,
    bandColor: BAND_COLORS[item.band]
  }))

  // Calculate trend line data
  const trendData = showTrendLine ? calculateTrendLine(chartData) : []

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload
      return (
        <div className="bg-white dark:bg-gray-800 p-4 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg">
          <p className="font-medium text-gray-900 dark:text-white mb-2">{label}</p>
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <div 
                className="w-3 h-3 rounded-full" 
                style={{ backgroundColor: data.bandColor }}
              />
              <span className="text-sm text-gray-600 dark:text-gray-400">
                Score: <span className="font-medium text-gray-900 dark:text-white">{data.score}</span>
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">
                Band: <span className="font-medium" style={{ color: data.bandColor }}>{data.band}</span>
              </span>
            </div>
          </div>
        </div>
      )
    }
    return null
  }

  const CustomDot = (props: any) => {
    const { cx, cy, payload } = props
    return (
      <circle
        cx={cx}
        cy={cy}
        r={4}
        fill={payload.bandColor}
        stroke="#fff"
        strokeWidth={2}
        className="drop-shadow-sm"
      />
    )
  }

  return (
    <div className={className}>
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
          <CartesianGrid 
            strokeDasharray="3 3" 
            stroke="#e5e7eb"
            className="dark:stroke-gray-600"
          />
          
          <XAxis 
            dataKey="date"
            tick={{ fontSize: 12, fill: '#6b7280' }}
            className="dark:fill-gray-400"
            axisLine={{ stroke: '#d1d5db' }}
            tickLine={{ stroke: '#d1d5db' }}
          />
          
          <YAxis 
            domain={[0, 100]}
            tick={{ fontSize: 12, fill: '#6b7280' }}
            className="dark:fill-gray-400"
            axisLine={{ stroke: '#d1d5db' }}
            tickLine={{ stroke: '#d1d5db' }}
          />

          {/* Band reference lines */}
          {showBands && Object.entries(BAND_THRESHOLDS).map(([band, threshold]) => (
            threshold > 0 && threshold < 100 && (
              <ReferenceLine
                key={band}
                y={threshold}
                stroke={BAND_COLORS[band as ScoreBand]}
                strokeDasharray="2 2"
                strokeOpacity={0.3}
              />
            )
          ))}

          <Tooltip content={<CustomTooltip />} />
          
          {/* Main score line */}
          <Line
            type="monotone"
            dataKey="score"
            stroke="#3b82f6"
            strokeWidth={3}
            dot={<CustomDot />}
            activeDot={{ r: 6, stroke: '#3b82f6', strokeWidth: 2, fill: '#fff' }}
          />

          {/* Trend line */}
          {showTrendLine && trendData.length > 0 && (
            <Line
              type="linear"
              dataKey="trend"
              data={trendData}
              stroke="#9ca3af"
              strokeWidth={2}
              strokeDasharray="5 5"
              dot={false}
              activeDot={false}
            />
          )}
        </LineChart>
      </ResponsiveContainer>
    </div>
  )
}

// Helper function to calculate trend line using linear regression
function calculateTrendLine(data: any[]): any[] {
  if (data.length < 2) return []

  const n = data.length
  const sumX = data.reduce((sum, _, i) => sum + i, 0)
  const sumY = data.reduce((sum, item) => sum + item.score, 0)
  const sumXY = data.reduce((sum, item, i) => sum + i * item.score, 0)
  const sumXX = data.reduce((sum, _, i) => sum + i * i, 0)

  const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX)
  const intercept = (sumY - slope * sumX) / n

  return data.map((item, i) => ({
    ...item,
    trend: slope * i + intercept
  }))
}